import React, { useState, useEffect } from 'react';
/**
 * `LandingPage` is the entry page for the dealer journey.
 *
 * Behaviour:
 * - Supports starting a new quote via VRN and/or VIN (depending on configuration).
 * - Supports searching for existing quotes by VRN, VIN, or email when quotes
 *   are enabled.
 * - Validates inputs (required + max length + email format) and displays
 *   inline error messages.
 * - Calls `QuotesApi` to fetch quote data and navigates to vehicle details when
 *   a quote is found.
 * - Supports manual vehicle details entry via `VehicleDetailsManual` when VIN
 *   input is enabled.
 */
import { Helmet } from 'react-helmet';
import { generatePath, useNavigate } from 'react-router-dom';
import NavBar from '../common/NavBar';
import RegistrationInput from '../common/RegistrationInput';
import { PageAbsolutePath, VehicleIdType } from '../../consts/Common';
import PageHeader from '../common/PageHeader';
import {
  ManualQuerySelections,
  QuotesData,
  VehicleType
} from '../common/CommonTypes';
import { getFormattedDate } from '../common/HelperMethods';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { QuotesApi } from '../../api/QuotesApi';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import TextInput from '../common/TextInput/TextInput';
import { useTranslation } from 'react-i18next';
import VehicleDetailsManual from '../common/VehiclePage/VehicleDetailsManual';
import { blankVehicle } from '../testData/journeyHelper';
import { useAppDispatch, useAppSelector } from '../../app-state/Hooks';
import { setLoggedIn } from '../../app-state/slices/UserSlice';

interface NewQuoteErrorObject {
  newQuoteVRN: string;
  newQuoteVIN: string;
  newQuoteError: string;
}

interface SearchQuoteErrorObject {
  searchQuoteEmail: string;
  searchQuoteVRN: string;
  searchQuoteError: string;
  searchQuoteVIN: string;
}

interface ValidationResultObject {
  isValid: boolean;
  validationMessage: string;
}

enum Status {
  Idle,
  GetStarted,
  NewQuoteByVrn,
  NewQuoteByVin,
  SearchQuoteByVrn,
  SearchQuoteByVin,
  SearchQuoteByEmail
}

const LandingPage = (): JSX.Element => {
  const { t } = useTranslation();
  const [detailsSetManually, setDetailsSetManually] = useState(false);
  const [newQuoteVRN, setNewQuoteVRN] = useState('');
  const [searchQuoteVRN, setSearchQuoteVRN] = useState('');
  const [newQuoteVIN, setNewQuoteVIN] = useState('');
  const [searchQuoteVIN, setSearchQuoteVIN] = useState('');
  const [searchQuoteEmail, setSearchQuoteEmail] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector((s) => s.user);
  const appConfig = useAppSelector((s) => s.appConfig);
  const cms = useAppSelector((s) => s.content.landingPage);

  const [quoteData, setQuoteData] = useState<QuotesData | null>(null);
  const [quoteSearchCompleted, setQuoteSearchCompleted] = useState(false);
  const [status, setStatus] = useState<Status>(Status.Idle);

  const vehicleRegistrationLabel = t(
    'landing-page.dj-vehicle-registration-label'
  );
  const quoteVinLabel = t('landing-page.dj-quote-vin');

  const [newQuoteValidationErrors, setNewQuoteValidationErrors] =
    useState<NewQuoteErrorObject>({
      newQuoteVRN: '',
      newQuoteVIN: '',
      newQuoteError: ''
    });

  const [searchQuoteValidationErrors, setSearchQuoteValidationErrors] =
    useState<SearchQuoteErrorObject>({
      searchQuoteEmail: '',
      searchQuoteVRN: '',
      searchQuoteError: '',
      searchQuoteVIN: ''
    });

  const [vehicleDetails, setVehicleDetails] =
    useState<VehicleType>(blankVehicle);

  const login = ({ state }: { state: object }) => {
    const path = generatePath(PageAbsolutePath.VehicleDetails, {
      countrycode: appConfig.countryCode
    });
    navigate(path, {
      state
    });
    dispatch(setLoggedIn(true));
  };

  useEffect(() => {
    if (detailsSetManually) {
      login({
        state: {
          vehicle: vehicleDetails
        }
      });
    }
  }, [detailsSetManually]);

  useEffect(() => {
    async function searchAsync() {
      const controller = new AbortController();

      switch (status) {
        case Status.NewQuoteByVrn:
          await searchQuoteAsync('', newQuoteVRN, '', true, controller.signal);
          break;
        case Status.NewQuoteByVin:
          await searchQuoteAsync('', '', newQuoteVIN, true, controller.signal);
          break;
        case Status.SearchQuoteByVrn:
          await searchQuoteAsync(
            '',
            searchQuoteVRN,
            '',
            false,
            controller.signal
          );
          break;
        case Status.SearchQuoteByVin:
          await searchQuoteAsync(
            '',
            '',
            searchQuoteVIN,
            false,
            controller.signal
          );
          break;
        case Status.SearchQuoteByEmail:
          await searchQuoteAsync(
            searchQuoteEmail,
            '',
            '',
            false,
            controller.signal
          );
          break;
      }

      return () => {
        controller.abort();
      };
    }

    searchAsync();
  }, [status]);

  useEffect(() => {
    if (quoteData) {
      login({
        state: {
          quote: quoteData
        }
      });
    }
  }, [quoteData]);

  useEffect(() => {
    if (quoteSearchCompleted) {
      if (status === Status.NewQuoteByVrn || status == Status.NewQuoteByVin) {
        if (newQuoteVRN !== '' && newQuoteValidationErrors.newQuoteVRN === '') {
          login({
            state: {
              idValue: newQuoteVRN,
              vehicleIdType: VehicleIdType.Vrn
            }
          });
        } else if (
          newQuoteVIN !== '' &&
          newQuoteValidationErrors.newQuoteVIN === ''
        ) {
          login({
            state: {
              idValue: newQuoteVIN,
              vehicleIdType: VehicleIdType.Vin
            }
          });
        }
      } else {
        setSearchQuoteValidationErrors({
          ...searchQuoteValidationErrors,
          searchQuoteError: t('landing-page.error-no-quotes')
        });
        setQuoteSearchCompleted(false);
      }
      setStatus(Status.Idle);
    }
  }, [quoteSearchCompleted]);

  const validateVrn = (vrn: string): ValidationResultObject => {
    let validationMessage = '';
    const vrnCharLimit = 10;

    if (CommonValidation.isEmpty(vrn)) {
      validationMessage = t('landing-page.error-empty-vrn');
    } else if (!CommonValidation.isValidLength(vrn, vrnCharLimit)) {
      validationMessage = t('landing-page.error-exceeds-length', {
        charLimit: vrnCharLimit
      });
    }

    if (validationMessage === '') {
      return {
        isValid: true,
        validationMessage
      };
    } else {
      return {
        isValid: false,
        validationMessage
      };
    }
  };

  const validateVin = (vinParam: string): ValidationResultObject => {
    let validationMessage = '';
    const vinCharLimit = 20;

    if (CommonValidation.isEmpty(vinParam)) {
      validationMessage = t('landing-page.error-empty-vin');
    } else if (!CommonValidation.isValidLength(vinParam, vinCharLimit)) {
      validationMessage = t('landing-page.error-exceeds-length', {
        charLimit: vinCharLimit
      });
    }

    if (validationMessage === '') {
      return { isValid: true, validationMessage };
    } else {
      return {
        isValid: false,
        validationMessage
      };
    }
  };

  const validateEmail = (): ValidationResultObject => {
    if (CommonValidation.isEmpty(searchQuoteEmail)) {
      return {
        isValid: false,
        validationMessage: t('landing-page.error-empty-email')
      };
    }

    if (!CommonValidation.isValidEmail(searchQuoteEmail)) {
      return {
        isValid: false,
        validationMessage: t('landing-page.error-invalid-email')
      };
    }
    return { isValid: true, validationMessage: '' };
  };

  const fetchQuotesSuccess = (quote: QuotesData) => {
    if (quote) {
      setQuoteData(quote);
    } else {
      setQuoteSearchCompleted(true);
    }
  };

  const fetchQuotesFailure = () => {
    console.error('Error occured while retrieving quotes');
    setQuoteSearchCompleted(true);
  };

  console.log('countryCode-' + appConfig.countryCode);

  const searchQuoteAsync = async (
    email: string,
    vrn: string,
    vin: string,
    isNewSearch: boolean,
    signal: AbortSignal
  ) => {
    if (email !== '') {
      new QuotesApi().getQuotesDatafromEmail(
        email,
        user.dealerCode,
        appConfig.countryCode,
        user.token,
        fetchQuotesSuccess,
        fetchQuotesFailure,
        signal
      );
      return;
    }

    if (vrn !== '') {
      new QuotesApi().getQuotesDatafromVrn(
        vrn,
        isNewSearch,
        user.dealerCode,
        appConfig.countryCode,
        user.token,
        fetchQuotesSuccess,
        fetchQuotesFailure,
        signal
      );
      return;
    }

    if (vin !== '') {
      new QuotesApi().getQuotesDatafromVin(
        vin,
        isNewSearch,
        user.dealerCode,
        appConfig.countryCode,
        user.token,
        fetchQuotesSuccess,
        fetchQuotesFailure,
        signal
      );
      return;
    }
  };

  const onSearchQuoteClick = () => {
    setStatus(Status.GetStarted);

    const newValidationErrors: SearchQuoteErrorObject = {
      searchQuoteEmail: '',
      searchQuoteVRN: '',
      searchQuoteVIN: '',
      searchQuoteError: ''
    };

    setSearchQuoteValidationErrors({ ...newValidationErrors });

    if (
      searchQuoteEmail === '' &&
      searchQuoteVRN === '' &&
      searchQuoteVIN === ''
    ) {
      newValidationErrors.searchQuoteError = t(
        'landing-page.error-invalid-details'
      );
    } else {
      let result: ValidationResultObject = validateEmail();

      if (result.isValid) {
        setStatus(Status.SearchQuoteByEmail);
        return;
      } else {
        newValidationErrors.searchQuoteEmail = result.validationMessage;
      }

      result = validateVrn(searchQuoteVRN);

      if (result.isValid) {
        setStatus(Status.SearchQuoteByVrn);
        return;
      } else {
        newValidationErrors.searchQuoteVRN = result.validationMessage;
      }

      result = validateVin(searchQuoteVIN);

      if (result.isValid) {
        setStatus(Status.SearchQuoteByVin);
        return;
      } else {
        newValidationErrors.searchQuoteVIN = result.validationMessage;
      }
    }
    setSearchQuoteValidationErrors({
      ...newValidationErrors
    });

    setStatus(Status.Idle);
  };

  const getStarted = () => {
    setStatus(Status.GetStarted);

    if (appConfig.includeQuotes) {
      getStartedWithQuotesOn();
    } else {
      if (cms.useVinInput) getStartedWithQuotesOn();
      else getStartedWithQuotesOff();
    }
  };

  const getStartedWithQuotesOn = () => {
    const newValidationErrors: NewQuoteErrorObject = {
      newQuoteVRN: '',
      newQuoteVIN: '',
      newQuoteError: ''
    };

    if (newQuoteVIN === '' && newQuoteVRN === '') {
      newValidationErrors.newQuoteError = !cms.useVinInput
        ? t('landing-page.error-empty-details')
        : t('landing-page.error-empty-details-no-manual-search');
    } else {
      let result: ValidationResultObject = validateVrn(newQuoteVRN);

      if (result.isValid) {
        setStatus(Status.NewQuoteByVrn);
        return;
      } else {
        newValidationErrors.newQuoteVRN = result.validationMessage;
      }

      result = validateVin(newQuoteVIN);

      if (result.isValid) {
        setStatus(Status.NewQuoteByVin);
        return;
      } else {
        newValidationErrors.newQuoteVIN = result.validationMessage;
      }
    }

    setNewQuoteValidationErrors(newValidationErrors);
    setStatus(Status.Idle);
  };

  const getStartedWithQuotesOff = () => {
    const result = validateVrn(newQuoteVRN);
    if (result.isValid) {
      login({
        state: {
          idValue: newQuoteVRN,
          vehicleIdType: VehicleIdType.Vrn
        }
      });
    } else {
      setNewQuoteValidationErrors({
        ...newQuoteValidationErrors,
        newQuoteVRN: result.validationMessage
      });
    }

    setStatus(Status.Idle);
  };

  const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      switch (e.currentTarget.name) {
        case 'searchQuoteEmail':
        case 'searchQuoteVRN':
        case 'searchQuoteVIN':
          onSearchQuoteClick();
          break;
        case 'newQuoteVIN':
        case 'newQuoteVRN':
          getStarted();
          break;
      }
    }
  };

  const onChangeVehicleReg = (e: React.FormEvent<HTMLInputElement>) => {
    setNewQuoteVRN(e.currentTarget.value);
  };

  const onChangeSearchQuoteVRN = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchQuoteVRN(e.currentTarget.value);
  };

  const onChangeVin = (e: React.FormEvent<HTMLInputElement>) => {
    setNewQuoteVIN(e.currentTarget.value);
  };

  const onChangeSearchQuoteVin = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchQuoteVIN(e.currentTarget.value);
  };

  const onChangeEmail = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchQuoteEmail(e.currentTarget.value);
  };

  const getCardClass = () => {
    let str = '';
    if (appConfig.includeQuotes === true)
      str = 'card product-card product-card-height';
    return str;
  };

  const getFirstRegistrationDate = (
    manualQuerySelections: ManualQuerySelections
  ) => {
    if (manualQuerySelections.firstRegistrationDate) {
      return getFormattedDate(manualQuerySelections.firstRegistrationDate);
    } else {
      return new Date().toLocaleDateString(appConfig.cultureCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  };

  const onConfirmManualVehicleModal = (
    manualQuerySelections: ManualQuerySelections,
    enginePowerBHP?: number,
    enginePowerKWH?: number,
    driveTypeId?: number,
    vin?: string,
    modelType?: string,
    fuelTypeCode?: string
  ) => {
    const newVehicleDetails: VehicleType = {
      ...vehicleDetails,
      registrationDate: getFirstRegistrationDate(manualQuerySelections),
      make: manualQuerySelections.make,
      model: manualQuerySelections.model,
      fuelType: manualQuerySelections.fuelType,
      engineSize: manualQuerySelections.engineSize,
      modelTrimId: parseInt(manualQuerySelections.vehicleModelTrimId),
      vin: vin,
      fuelTypeId: parseInt(manualQuerySelections.vehicleFuelTypeId),
      fuelTypeCode: fuelTypeCode ?? '',
      enginePowerBHP: enginePowerBHP,
      enginePowerKWH: enginePowerKWH,
      driveTypeId: driveTypeId,
      errorMessage: '',
      modelType: modelType ?? '',
      makeId: manualQuerySelections.vehicleMakeId,
      vehicleTypeId: manualQuerySelections.vehicleTypeId,
      modelId: manualQuerySelections.vehicleModelId
    };
    setVehicleDetails(newVehicleDetails);
    setDetailsSetManually(true);
  };

  const getControlLabelClass = (error: string) => {
    return CommonValidation.isEmpty(error)
      ? 'control-label'
      : 'control-label control-label-error';
  };

  const getFormNavClass = (error: string) => {
    return CommonValidation.isEmpty(error)
      ? 'form-nav'
      : 'form-nav form-nav-error';
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.login')}</title>
      </Helmet>

      <div className='flex-column main-column'>
        <div className='main-content'>
          <NavBar />
          <div className='flex-row'>
            <div className='page-container'>
              <div className='page-header-line' />
              <PageHeader
                title={cms.title}
                description={cms.description}
                information={cms.information}
                warning={cms.warning}
                descriptionClass='p2'
              />
              <div className='form'>
                <div className='flex-row'>
                  <div className='flex-column'>
                    <div className={getCardClass()}>
                      <div className='product-card-header'>
                        <h4>
                          {appConfig.includeQuotes
                            ? t('landing-page.dj-new-quote')
                            : ''}
                        </h4>
                      </div>
 
                      <label className='control-label' htmlFor='newQuoteVRN'>
                        {vehicleRegistrationLabel}
                      </label>
                      <div className='control'>
                        <RegistrationInput
                          label={t('landing-page.vehicle-registration')}
                          name='newQuoteVRN'
                          className='input-text'
                          onChange={(e: React.FormEvent<HTMLInputElement>) =>
                            onChangeVehicleReg(e)
                          }
                          onKeyPress={(
                            e: React.KeyboardEvent<HTMLInputElement>
                          ) => handleEnterKeyPress(e)}
                          value={newQuoteVRN}
                          error={newQuoteValidationErrors.newQuoteVRN}
                          hasError={newQuoteValidationErrors.newQuoteVRN !== ''}
                        />
                      </div>
                      {(appConfig.includeQuotes || cms.useVinInput) && (
                        <>
                          <label className='control-label' htmlFor='newQuoteVIN'>
                            {quoteVinLabel}
                          </label>
                          <div className='control'>
                            <TextInput
                              placeholder={t('landing-page.dj-quote-vin')}
                              name='newQuoteVIN'
                              className='input-text w201'
                              onChange={(
                                e: React.FormEvent<HTMLInputElement>
                              ) => onChangeVin(e)}
                              onKeyPress={(
                                e: React.KeyboardEvent<HTMLInputElement>
                              ) => handleEnterKeyPress(e)}
                              value={newQuoteVIN}
                              error={newQuoteValidationErrors.newQuoteVIN}
                            />
                          </div>
                          {cms.useVinInput && (
                            <div className='vehcile-details-manual-padding'>
                              <VehicleDetailsManual
                                onConfirmManualVehicleModal={
                                  onConfirmManualVehicleModal
                                }
                                isFRDOptional={true}
                                culture={appConfig.cultureCode}
                              />
                            </div>
                          )}
                        </>
                      )}
                      <div className='form-nav'>
                        <>
                          <div>
                            <ButtonContinue
                              name='btnGetStarted'
                              text={t('customer-button.button-get-started')}
                              onClick={getStarted}
                              disabled={status !== Status.Idle}
                            />
                          </div>
                          <div className='error-style'>
                            {newQuoteValidationErrors.newQuoteError}
                          </div>
                        </>
                      </div>
                    </div>
                  </div>
                  {appConfig.includeQuotes && (
                    <div className='flex-column'>
                      <div className='card product-card product-card-height'>
                        <div className='product-card-header'>
                          <h4>{t('landing-page.dj-search-quote')}</h4>
                        </div>
                        <label
                          className={getControlLabelClass(
                            searchQuoteValidationErrors.searchQuoteVRN
                          )}
                          htmlFor='searchQuoteVRN'>
                          {vehicleRegistrationLabel}
                        </label>
                        <div className='control'>
                          <RegistrationInput
                            label={t('landing-page.vehicle-registration')}
                            name='searchQuoteVRN'
                            className='input-text'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              onChangeSearchQuoteVRN(e)
                            }
                            onKeyPress={(
                              e: React.KeyboardEvent<HTMLInputElement>
                            ) => handleEnterKeyPress(e)}
                            value={searchQuoteVRN}
                            error={searchQuoteValidationErrors.searchQuoteVRN}
                            hasError={
                              searchQuoteValidationErrors.searchQuoteVRN !== ''
                            }
                          />
                        </div>
                        <label
                          className={getControlLabelClass(
                            searchQuoteValidationErrors.searchQuoteVIN
                          )}
                          htmlFor='searchQuoteVIN'>
                          {quoteVinLabel}
                        </label>
                        <div className='control'>
                          <TextInput
                            placeholder={t('landing-page.dj-quote-vin')}
                            name='searchQuoteVIN'
                            className='input-text w201'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              onChangeSearchQuoteVin(e)
                            }
                            onKeyPress={(
                              e: React.KeyboardEvent<HTMLInputElement>
                            ) => handleEnterKeyPress(e)}
                            value={searchQuoteVIN}
                            error={searchQuoteValidationErrors.searchQuoteVIN}
                          />
                        </div>
                        <label
                          className='control-label'
                          htmlFor='searchQuoteEmail'>
                          {t('landing-page.dj-quote-email')}
                        </label>
                        <div className='control'>
                          <TextInput
                            placeholder={t('landing-page.dj-quote-email')}
                            name='searchQuoteEmail'
                            className='input-text w250'
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              onChangeEmail(e)
                            }
                            onKeyPress={(
                              e: React.KeyboardEvent<HTMLInputElement>
                            ) => handleEnterKeyPress(e)}
                            value={searchQuoteEmail}
                            error={searchQuoteValidationErrors.searchQuoteEmail}
                          />
                        </div>
                        <div
                          className={getFormNavClass(
                            searchQuoteValidationErrors.searchQuoteVIN
                          )}>
                          <>
                            <div>
                              <ButtonContinue
                                name='btnSearchQuote'
                                text={t('customer-button.button-search-quote')}
                                onClick={onSearchQuoteClick}
                                disabled={status !== Status.Idle}
                              />
                            </div>
                            <div className='error-style'>
                              {searchQuoteValidationErrors.searchQuoteError}
                            </div>
                          </>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='flex-column desktop-column'>
        <div className='desktop-content-full'>
          <img
            className='landing-image'
            src={cms.backgroundImageUrl}
            alt={cms.backgroundImageAltText}
          />
        </div>
      </div>
    </>
  );
};

export default LandingPage;
